import { skipToken, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdDriversTypeahead,
  GetV1CompaniesCompanyIdDriversTypeaheadData,
} from '@treadinc/horizon-api-spec';
import { useCallback, useMemo, useState } from 'react';

import { DriverTypeahead } from '~hooks/useDrivers/models';
import { extractPagination } from '~services/pagination';

import {
  InfiniteQuery,
  InfiniteQueryFetchReason,
  InfiniteQueryParamsState,
} from './constants';
import { deriveInfiniteQueryBaseParams } from './utils';

export default function useDriversInfiniteQuery(
  queryKey: string,
  companyId: string,
): InfiniteQuery {
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<InfiniteQueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const { isFetching, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters.query, companyId],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: queryParams.shouldFetch
      ? async ({ pageParam, signal }) => {
          const query: GetV1CompaniesCompanyIdDriversTypeaheadData['query'] =
            deriveInfiniteQueryBaseParams({ ...queryParams, ...pageParam });

          const response = await getV1CompaniesCompanyIdDriversTypeahead({
            path: { 'company-id': companyId },
            query,
            signal,
          });

          const drivers = response.data.data.map((driver) => {
            const parsed = DriverTypeahead.parse(driver);

            return { label: parsed.fullName, value: parsed.id };
          });
          const pagination = extractPagination(response);

          setQueryParams((state) => ({
            ...state,
            pagination: { ...pagination, hasMore: Boolean(pagination.after) },
          }));

          return drivers;
        }
      : skipToken,
  });

  const allData = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const fetchReason = useMemo(() => {
    if (isFetchingNextPage) {
      return InfiniteQueryFetchReason.INFINITE_SCROLL;
    }

    if (isFetching) {
      return InfiniteQueryFetchReason.SEARCH_VALUE;
    }
  }, [isFetchingNextPage, isFetching]);

  const fetch = useCallback((searchQuery: string) => {
    setQueryParams({ shouldFetch: true, filters: { query: searchQuery } });
  }, []);

  const fetchMore = useMemo(() => {
    if (queryParams.pagination?.hasMore) {
      return fetchNextPage;
    }
  }, [queryParams.pagination?.hasMore, fetchNextPage]);

  const clear = useCallback(() => {
    setQueryParams({ shouldFetch: false, filters: { query: '' } });

    queryClient.setQueriesData({ queryKey: [queryKey] }, () => ({
      pages: [],
      pageParams: [],
    }));
  }, [queryKey]);

  return { clear, data: allData, fetch, fetchMore, fetchReason, isDummyQuery: false };
}
